import type { SessionView } from "../stores/session.ts";
import { type Signer, turnkeySigner, walletSigner } from "./signer.ts";
import { type WalletHandle, discover } from "./wallets.ts";

/**
 * ⚠️ WHICH KEY IS SIGNING RIGHT NOW, AND IT IS NOT THE SESSION'S JOB TO KNOW.
 *
 * A session says WHO is signed in — an address, and a bearer that proves it. It survives a reload because
 * it is written down. A connected extension does NOT: the extension is a separate process, the page's
 * permission to talk to it is re-established per load, and nothing we store changes that. So the two are
 * tracked apart, and a returning user with a wallet session is asked to reconnect rather than shown a Buy
 * button that cannot sign.
 *
 * `subOrgId !== null` used to be the app's test for "can sign", which was true while turnkey was the only
 * signer and quietly excludes every external wallet. `signerFor` is that question asked properly.
 */
let connected: WalletHandle | null = null;
let connectedAddress: string | null = null;

const listeners = new Set<() => void>();

const announce = (): void => {
  for (const listener of listeners) listener();
};

export const onWalletChange = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const availableWallets = (): WalletHandle[] => discover();

export const connectWallet = async (handle: WalletHandle): Promise<string> => {
  const address = await handle.connect();
  connected = handle;
  connectedAddress = address;
  announce();
  return address;
};

export const disconnectWallet = (): void => {
  connected = null;
  connectedAddress = null;
  announce();
};

export const connectedWallet = (): { name: string; address: string } | null =>
  connected === null || connectedAddress === null
    ? null
    : { name: connected.name, address: connectedAddress };

/**
 * The signer for this session, or null when nothing here can sign for that address.
 *
 * ⚠️ THE ADDRESS MUST MATCH. A connected wallet whose account was switched in the extension is a DIFFERENT
 * key, and signing with it produces a valid signature by the wrong party — which the server refuses far
 * from the cause. Answering null instead sends the user back to reconnect, which is the truth.
 */
export const signerFor = (session: SessionView): Signer | null => {
  const address = session.address;
  if (address === null) return null;
  if (connected !== null && connectedAddress === address) return walletSigner(connected, address);
  if (session.subOrgId !== null) return turnkeySigner(session.subOrgId, address);
  return null;
};

/**
 * ⚠️ NOT the session store's `canSign`, which asks whether the SESSION is live enough to sign. This asks
 * whether anything here CAN — a live session with no connected wallet and no sub-org cannot. Both are real
 * questions and a call site usually wants both.
 */
export const haveSigner = (session: SessionView): boolean => signerFor(session) !== null;
